import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Chip,
  Divider,
} from '@mui/material';
import { type ModifierGroup, type ModifierOption } from '@resort-os/api-client';
import { useFormatCurrency } from '@resort-os/ui';

interface ModifierPickerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedOptions: { groupName: string; options: ModifierOption[] }[]) => void;
  productName: string;
  productBasePrice: number;
  modifierGroups: ModifierGroup[];
}

const BORDER = '1px solid #E2DDD8';

export default function ModifierPicker({
  open,
  onClose,
  onConfirm,
  productName,
  productBasePrice,
  modifierGroups,
}: ModifierPickerProps) {
  const formatCurrency = useFormatCurrency();
  const [selections, setSelections] = useState<Record<string, string[]>>({});

  React.useEffect(() => {
    if (open) {
      const initial: Record<string, string[]> = {};
      modifierGroups.forEach(group => {
        if (group.required && group.options.length === 1) {
          initial[group.name] = [group.options[0].name];
        } else {
          initial[group.name] = [];
        }
      });
      setSelections(initial);
    }
  }, [open, modifierGroups]);

  const totalPriceModifier = useMemo(() => {
    let total = 0;
    Object.entries(selections).forEach(([groupName, optionNames]) => {
      const group = modifierGroups.find(g => g.name === groupName);
      if (group) {
        optionNames.forEach(optName => {
          const option = group.options.find(o => o.name === optName);
          if (option) total += option.price_modifier;
        });
      }
    });
    return total;
  }, [selections, modifierGroups]);

  const finalPrice = productBasePrice + totalPriceModifier;

  const handleSelect = (groupName: string, optionName: string) => {
    const group = modifierGroups.find(g => g.name === groupName);
    if (!group) return;

    setSelections(prev => {
      const current = prev[groupName] || [];
      if (group.multi_select) {
        const exists = current.includes(optionName);
        return {
          ...prev,
          [groupName]: exists ? current.filter(o => o !== optionName) : [...current, optionName],
        };
      } else {
        return { ...prev, [groupName]: [optionName] };
      }
    });
  };

  const isValid = modifierGroups.every(group => {
    if (!group.required) return true;
    return (selections[group.name]?.length || 0) > 0;
  });

  const handleConfirm = () => {
    const result = modifierGroups
      .filter(g => (selections[g.name]?.length || 0) > 0)
      .map(g => ({
        groupName: g.name,
        options: g.options.filter(o => selections[g.name]?.includes(o.name)),
      }));
    onConfirm(result);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { border: BORDER, borderRadius: 0 } }}
    >
      <DialogTitle
        sx={{
          borderBottom: BORDER,
          pb: 2,
          color: '#2C3E2D',
          fontSize: '1.1rem',
        }}
      >
        Customize — {productName}
      </DialogTitle>
      <DialogContent sx={{ pt: 5, mt: 2 }}>
        {modifierGroups.map((group, idx) => (
          <Box key={group.name} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  color: '#2C3E2D',
                }}
              >
                {group.name}
              </Typography>
              {group.required && (
                <Chip
                  label="Required"
                  size="small"
                  sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#FFF3E0', color: '#E65100' }}
                />
              )}
              {group.multi_select && (
                <Chip
                  label="Multi"
                  size="small"
                  sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#E3F2FD', color: '#0D47A1' }}
                />
              )}
            </Box>
            {group.multi_select ? (
              <FormGroup>
                {group.options.map(option => (
                  <FormControlLabel
                    key={option.name}
                    control={
                      <Checkbox
                        checked={selections[group.name]?.includes(option.name) || false}
                        onChange={() => handleSelect(group.name, option.name)}
                        size="small"
                        sx={{ color: '#2C3E2D', '&.Mui-checked': { color: '#2C3E2D' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Typography sx={{ fontSize: '0.78rem' }}>{option.name}</Typography>
                        {option.price_modifier > 0 && (
                          <Typography sx={{ fontSize: '0.72rem', color: '#6B6560' }}>
                            +{formatCurrency(option.price_modifier)}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ mb: 0.5, width: '100%' }}
                  />
                ))}
              </FormGroup>
            ) : (
              <RadioGroup
                value={selections[group.name]?.[0] || ''}
                onChange={(e, val) => handleSelect(group.name, val)}
              >
                {group.options.map(option => (
                  <FormControlLabel
                    key={option.name}
                    value={option.name}
                    control={
                      <Radio
                        size="small"
                        sx={{ color: '#2C3E2D', '&.Mui-checked': { color: '#2C3E2D' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Typography sx={{ fontSize: '0.78rem' }}>{option.name}</Typography>
                        {option.price_modifier > 0 && (
                          <Typography sx={{ fontSize: '0.72rem', color: '#6B6560' }}>
                            +{formatCurrency(option.price_modifier)}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ mb: 0.5, width: '100%' }}
                  />
                ))}
              </RadioGroup>
            )}
          </Box>
        ))}
        {totalPriceModifier > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#6B6560' }}>Base price</Typography>
              <Typography sx={{ fontSize: '0.78rem' }}>
                {formatCurrency(productBasePrice)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
            >
              <Typography sx={{ fontSize: '0.78rem', color: '#6B6560' }}>Modifiers</Typography>
              <Typography sx={{ fontSize: '0.78rem' }}>
                +{formatCurrency(totalPriceModifier)}
              </Typography>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Final price</Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: '#2C3E2D',
                }}
              >
                {formatCurrency(finalPrice)}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: BORDER, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 0,
            border: BORDER,
            color: '#6B6560',
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!isValid}
          variant="contained"
          sx={{
            bgcolor: '#2C3E2D',
            borderRadius: 0,
            px: 3,

            '&:hover': { bgcolor: '#1a2d1b' },
          }}
        >
          Add to Cart — {formatCurrency(finalPrice)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
